import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Grid,
  Button,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

import { useApiGet } from '../../utils/httpClientTs';
import { searchUsers } from '../../endpoints/usersTs';
import { getSpds } from '../../endpoints/annualReports';
import { UserListItem, UserSearchRecord } from '../../types/users';
import { SortOrder } from '../../types/table';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../Components/ServerSideTable';
import SPDSelector from '../../Components/SPDSelector';
import UserCreateModal from '../../Components/UserCreateModal';
import { roles } from '../../utils/variables';

const UserList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (key: string, value: string) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (!value || value === '') {
        newSearchParams.delete(key);
      } else {
        newSearchParams.set(key, value);
      }
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState<keyof UserListItem>('last_name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [delaySearch, setDelaySearch] = useState(
    () => searchParams.get('search') || ''
  );
  const [userType, setUserType] = useState(
    () => searchParams.get('type') || 'all-members'
  );
  const [spd, setSpd] = useState(searchParams.get('spd') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Fetch SPD data for display names
  const { data: spdList } = useApiGet(
    ['spds', true],
    () => getSpds({ all: true }),
    {
      enabled: true,
      retry: 1,
    }
  );

  // Track previous values to prevent loops
  const prevSearch = useRef(search);
  const prevUserType = useRef(userType);
  const prevSpd = useRef(spd);

  // Handle URL parameter changes (only when URL changes externally)
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    const urlType = searchParams.get('type') || 'all-members';
    const urlSpd = searchParams.get('spd') || '';

    // Update state from URL if different
    if (urlSearch !== search) {
      setSearch(urlSearch);
      setDelaySearch(urlSearch);
    }
    if (urlType !== userType) {
      setUserType(urlType);
    }
    if (urlSpd !== spd) {
      setSpd(urlSpd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only depend on searchParams to avoid loops

  // Update URL when search changes (debounced)
  useEffect(() => {
    if (prevSearch.current !== search) {
      prevSearch.current = search;
      const urlSearch = searchParams.get('search') || '';
      if (search !== urlSearch) {
        updateSearchParams('search', search);
      }
    }
  }, [search, updateSearchParams, searchParams]);

  // Update URL when userType changes and reset pagination
  useEffect(() => {
    if (prevUserType.current !== userType) {
      prevUserType.current = userType;
      const urlType = searchParams.get('type') || 'all-members';
      if (userType !== urlType) {
        updateSearchParams('type', userType);
        setPage(0); // Reset to first page when filter changes
      }
    }
  }, [userType, updateSearchParams, searchParams]);

  // Update URL when spd changes and reset pagination
  useEffect(() => {
    if (prevSpd.current !== spd) {
      prevSpd.current = spd;
      const urlSpd = searchParams.get('spd') || '';
      if (spd !== urlSpd) {
        updateSearchParams('spd', spd);
        setPage(0); // Reset to first page when filter changes
      }
    }
  }, [spd, updateSearchParams, searchParams]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  // Map userType to member_status for Go server
  const mapUserTypeToMemberStatus = (
    userType: string
  ):
    | 'active'
    | 'inactive'
    | 'deceased'
    | 'non-members'
    | 'all'
    | 'all-members' => {
    switch (userType) {
      case 'all-members':
        return 'all-members';
      case 'all':
        return 'all';
      case 'member-active':
        return 'active';
      case 'member-inactive':
        return 'inactive';
      case 'deceased':
        return 'deceased';
      case 'non-member':
        return 'non-members';
      default:
        return 'all-members';
    }
  };

  const { data: rawData, isFetching } = useApiGet(
    ['users', rowsPerPage, page, search, userType, spd],
    () =>
      searchUsers({
        search: search || undefined,
        member_status: mapUserTypeToMemberStatus(userType),
        spd_chapter_id: spd || undefined,
        page: page, // 0-based for Go server
        per_page: rowsPerPage,
      }),
    {
      enabled: true,
      retry: 1,
    }
  );

  // Helper function to format member status for display
  const formatMemberStatus = (memberStatus: string): string => {
    switch (memberStatus) {
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      case 'deceased':
        return 'Deceased';
      case 'non-member':
        return 'Non-Member';
      default:
        return memberStatus;
    }
  };

  // Transform UserSearchRecord to UserListItem format
  const data = React.useMemo(() => {
    if (!rawData) return null;

    // Transform UserSearchRecord to UserListItem format
    const transformedData: UserListItem[] = rawData.data.map(
      (record: UserSearchRecord) => ({
        id: record.id,
        first_name: record.first_name,
        last_name: record.last_name,
        user_type: formatMemberStatus(record.member_status),
        last_chapter: record.current_chapter || '',
      })
    );

    // Apply client-side sorting if needed (Go server doesn't support orderBy/order)
    let sortedData = transformedData;
    if (orderBy) {
      sortedData = [...transformedData].sort((a, b) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];
        if (aValue < bValue) return order === 'asc' ? -1 : 1;
        if (aValue > bValue) return order === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return {
      data: sortedData,
      pagination: rawData.pagination,
    };
  }, [rawData, orderBy, order]);

  const handleClick = (user: UserListItem) => {
    window.location.href = `/user/${user.id}`;
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing page size
  };

  const handleSort = (property: keyof UserListItem) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const canCreate = () => {
    let role = localStorage.getItem('role');
    let rolesAccepted = [
      roles.Admin,
      roles.Support,
      roles.SPDOfficer,
      roles.LCOfficer,
      roles.Project,
    ];
    return rolesAccepted.includes(role);
  };

  const clearFilters = () => {
    setSearch('');
    setDelaySearch('');
    setUserType('all-members');
    setSpd('');
    setPage(0);
  };

  const hasActiveFilters = search || userType !== 'all-members' || spd;

  // Helper function to get user type display name
  const getUserTypeDisplayName = (type: string) => {
    switch (type) {
      case 'all-members':
        return 'All-Members';
      case 'all':
        return 'Non-member and Members';
      case 'member-active':
        return 'Active Members';
      case 'member-inactive':
        return 'Inactive Members';
      case 'deceased':
        return 'Deceased';
      case 'non-member':
        return 'Non-Members';
      default:
        return type;
    }
  };

  // Helper function to get SPD display name
  const getSpdDisplayName = (spdId: string) => {
    if (!spdId || !spdList) return spdId;
    const spdItem = spdList.find((spd) => spd.id === spdId);
    return spdItem ? spdItem.name : spdId;
  };

  const columns: ServerSideTableColumn<UserListItem>[] = [
    {
      id: 'first_name',
      label: 'First Name',
      width: '25%',
      sortable: true,
    },
    {
      id: 'last_name',
      label: 'Last Name',
      width: '25%',
      sortable: true,
    },
    {
      id: 'user_type',
      label: 'Type',
      width: '25%',
      sortable: true,
    },
    {
      id: 'last_chapter',
      label: 'Chapter',
      width: '25%',
      sortable: true,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Search and Basic Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              label="Search"
              id="search-field"
              size="small"
              value={delaySearch}
              fullWidth
              onChange={(event) => setDelaySearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Button
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              fullWidth
              size="small"
            >
              Filters
            </Button>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              fullWidth
              size="small"
            >
              Clear
            </Button>
          </Grid>

          {canCreate() && (
            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateModalOpen(true)}
                fullWidth
                sx={{
                  backgroundColor: '#00A859',
                  '&:hover': { backgroundColor: '#008a4a' },
                }}
              >
                Add User
              </Button>
            </Grid>
          )}
        </Grid>

        {/* Advanced Filters */}
        {showFilters && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="user-type-label">User Type</InputLabel>
                  <Select
                    labelId="user-type-label"
                    value={userType}
                    label="User Type"
                    onChange={(event) => setUserType(event.target.value)}
                  >
                    <MenuItem value="all-members">All-Members</MenuItem>
                    <MenuItem value="member-active">Active Members</MenuItem>
                    <MenuItem value="member-inactive">
                      Inactive Members
                    </MenuItem>
                    <MenuItem value="deceased">Deceased</MenuItem>
                    <MenuItem value="all">Non-members and Members</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <SPDSelector
                  spd={spd}
                  handleSpdSelect={setSpd}
                  reportId="user-list"
                  all={true}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Active Filters Display */}
      {(search || userType || spd) && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'action.hover' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Active Filters:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {search && (
              <Chip
                label={`Search: "${search}"`}
                onDelete={() => {
                  setSearch('');
                  setDelaySearch('');
                }}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            <Chip
              label={`Type: ${getUserTypeDisplayName(userType)}`}
              onDelete={() => setUserType('all-members')}
              color="secondary"
              variant="outlined"
              size="small"
            />
            {spd && (
              <Chip
                label={`SPD: ${getSpdDisplayName(spd)}`}
                onDelete={() => setSpd('')}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
          </Stack>
        </Paper>
      )}

      {/* Results Summary */}
      {data && data.data.length === 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No users found
            {search && ` matching "${search}"`}
            {userType !== 'all-members' &&
              ` (${getUserTypeDisplayName(userType)})`}
            {spd && ` in ${getSpdDisplayName(spd)}`}
          </Typography>
        </Box>
      )}

      <ServerSideTable
        data={data?.data || []}
        columns={columns}
        loading={isFetching}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.pagination?.total_count || 0}
        totalPages={data?.pagination?.total_pages || 0}
        hasNext={data?.pagination?.has_next || false}
        hasPrev={data?.pagination?.has_prev || false}
        order={order}
        orderBy={orderBy}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSort={handleSort}
        onRowClick={handleClick}
        emptyMessage="No users found"
      />
      <UserCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={(userId) => {
          setCreateModalOpen(false);
          // Optionally refresh the list or navigate
        }}
      />
    </Box>
  );
};

export default UserList;
